/**
 * 현장 운영 페이지의 '배포' 섹션.
 *
 * 메인 대시보드(SiteDashboardView)에서 분리된 배포 기능을 현장 페이지 내부로
 * 옮긴 것 — 한 현장에 대해서만 동작하므로 site 선택 UI 가 없다.
 *
 *  - 상단: 배포 대상 선택 (사내 테스트 서버 / 현장 통합서버) + 배포 시작 버튼.
 *    둘 다 GitHub Actions — 사내는 빌드해서 사내 서버로(company-deploy.yaml), 현장은
 *    사내에서 확인한 이미지를 현장 통합서버로 승격(site-deploy.yaml).
 *    대상 카드마다 실제로 어디로 가는지(사내에 지금 떠 있는 현장 / 현장 통합서버 IP)와
 *    그곳의 마지막 배포를 적는다.
 *  - 그 아래: 서비스별 실행 중 버전 (사내 서버 / 현장 통합서버) + 그 서비스의 현장 설정 파일
 *    (누르면 바로 수정) — RunningVersionsPanel
 *  - 그 아래: 자정 자동 배포 예약 (AutoDeployCard) — 서비스별 맞출 사내 버전, 마지막 실행 결과
 *  - 하단: 배포 히스토리 — 사내/현장 탭으로 나누고(섞으면 어디 버전인지 헷갈린다),
 *    서비스별로 거른다.
 *
 * 다이얼로그(`DeployConfirmDialog`)와 진행 모달(`SiteBatchProgressModal`),
 * 그리고 `useDeployActions` 훅을 그대로 재사용한다. auto-swap 은 다중 현장
 * 컨텍스트가 없으므로 빈 runningSites / sitesByName 으로 호출 — production
 * 에서는 `isLocalEnvironment()` 가 false 라 자동 no-op 이다.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Lan as LanIcon,
  Person as PersonIcon,
  PlayArrow as PlayIcon,
  Sensors as FieldIcon,
  CheckCircleOutline as CheckIcon,
  HighlightOff as FailIcon,
  RemoveCircleOutline as CancelledIcon,
  HourglassEmpty as InProgressIcon,
  OpenInNew as OpenInNewIcon,
  Apps as AppsIcon,
} from '@mui/icons-material';
import DeployConfirmDialog from '../../../views/SiteDashboardView/dialogs/DeployConfirmDialog';
import SiteBatchProgressModal from '../../../views/SiteDashboardView/dialogs/SiteBatchProgressModal';
import { AutoSwapConfirmDialog } from '../../../views/SiteDashboardView/dialogs/AutoSwapConfirmDialog';
import useDeployActions from '../../../views/SiteDashboardView/hooks/useDeployActions';
import {
  useRecentDeployments,
  RecentDeploymentItem,
} from '../../../views/SiteDashboardView/hooks/useRecentDeployments';
import type { DeployTabType } from '../../../views/SiteDashboardView/types';
import { useDashboardTokens } from '../../../views/SiteDashboardView/tokens';
import { Project } from '../../../models/Project';
import { RuntimeService } from '../../../services/RuntimeService';
import { SiteNetworkDeviceService } from '../../../services/SiteNetworkDeviceService';
import { integrationServers, serverLabel } from '../../../views/SiteDashboardView/hooks/remoteDeploy';
import RunningVersionsPanel from './RunningVersionsPanel';
import AutoDeployCard from './AutoDeployCard';
import { filterHistory, historyApps, latestDeploy } from './deployHistory';

/** 히스토리 표에 보이는 줄 수 — 거른 뒤 최근 것부터. */
const HISTORY_ROWS = 20;
/** 사내/현장·서비스로 거를 원본. 목록 API 는 현장의 배포를 전부 주므로 넉넉히 받는다. */
const HISTORY_FETCH = 500;

// 실패 row 의 GitHub Actions 링크는 luxrobo/Luzer 리포 고정. workflow_run id
// 가 있으면 직접 해당 run 페이지로, 없으면 작업 목록으로 fallback.
const GH_REPO = 'luxrobo/Luzer';
function githubRunUrl(runId: number | null | undefined): string {
  if (runId && runId > 0) {
    return `https://github.com/${GH_REPO}/actions/runs/${runId}`;
  }
  return `https://github.com/${GH_REPO}/actions`;
}

interface DeploySectionProps {
  projectId: number;
  projectName: string;
}

const TARGET_LABEL: Record<DeployTabType, string> = {
  company: '사내 서버로 배포 시작',
  remote: '현장 통합서버로 배포',
};

const SCOPE_LABEL: Record<DeployTabType, string> = { company: '사내', remote: '현장' };

const DeploySection: React.FC<DeploySectionProps> = ({ projectId, projectName }) => {
  const { colors, type, space } = useDashboardTokens();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [target, setTarget] = useState<DeployTabType>('company');

  // 이 섹션에서는 단일 현장만 대상으로 하므로 site selection UI 없이 직접 구성.
  // DeployConfirmDialog 가 요구하는 Project shape 의 필수 필드만 채워준다.
  const site: Project = useMemo(
    () => ({
      id: String(projectId),
      name: projectName,
      description: '',
      location: '',
    }),
    [projectId, projectName],
  );

  const actions = useDeployActions(target, {
    runningSites: [],
    sitesByName: new Map(),
  });

  const projectIdStr = String(projectId);
  const { data: recent, isLoading, isError, refetch } = useRecentDeployments(
    projectIdStr,
    { enabled: true, limit: HISTORY_FETCH },
  );
  const all = useMemo(() => recent ?? [], [recent]);

  // 히스토리는 고른 배포 대상 쪽을 먼저 보여 준다. 탭은 따로 바꿀 수 있다.
  const [historyScope, setHistoryScope] = useState<DeployTabType>(target);
  const [historyApp, setHistoryApp] = useState<string | null>(null);
  useEffect(() => {
    setHistoryScope(target);
  }, [target]);
  useEffect(() => {
    setHistoryApp(null);
  }, [historyScope]);
  const apps = useMemo(() => historyApps(all, historyScope), [all, historyScope]);
  const filtered = useMemo(() => filterHistory(all, historyScope, historyApp), [all, historyScope, historyApp]);

  // 대상 카드: 실제로 어디로 가는지. query key 는 버전 패널·배포 다이얼로그와 같아 캐시를 같이 쓴다.
  const company = useQuery({
    queryKey: ['runtime', 'service-versions'],
    queryFn: ({ signal }) => RuntimeService.getServiceVersions(signal),
    staleTime: 60_000,
    retry: false,
  });
  const devices = useQuery({
    queryKey: ['site-network-devices', projectId],
    queryFn: () => SiteNetworkDeviceService.list(projectId),
  });
  const servers = useMemo(() => integrationServers(devices.data ?? []), [devices.data]);

  const companyWhere = (() => {
    if (company.isLoading) return '사내 서버 확인 중…';
    if (!company.data) return '사내 서버 상태를 읽지 못함';
    if (!company.data.site) return '사내 서버 — 지금 정지됨';
    if (company.data.site === projectName) return `사내 서버 — 지금 ${projectName} 실행 중`;
    return `사내 서버 — 지금 ${company.data.site} 실행 중 · 배포하면 ${projectName} 로 바뀝니다`;
  })();
  const remoteWhere = devices.isLoading
    ? '통합서버 확인 중…'
    : servers.length === 0
      ? '통합서버 미등록 — 현장 설정에서 등록'
      : servers.map(serverLabel).join(', ');

  // 사내/현장을 페이지 전체에서 같은 색으로 — 버튼·모드 띠·아래 패널 테두리.
  const targetColor = target === 'remote' ? colors.warn : colors.accent;

  const handleTrigger = () => {
    setConfirmOpen(true);
  };
  const closeConfirm = () => setConfirmOpen(false);

  return (
    <Stack spacing={`${space.md}px`}>
      {/* 상단: 무엇을 어디로 배포하는지 + 배포 시작 */}
      <Stack
        component="header"
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'flex-end' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography
            sx={{
              fontSize: type.caption.size,
              fontWeight: type.caption.weight,
              letterSpacing: type.caption.spacing,
              textTransform: 'uppercase',
              color: colors.muted,
            }}
          >
            Deploy
          </Typography>
          <Typography
            component="h1"
            sx={{ fontSize: type.h1.size, fontWeight: type.h1.weight, letterSpacing: type.h1.spacing, color: colors.text, lineHeight: 1.15 }}
          >
            {projectName} 배포
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PlayIcon />}
          onClick={handleTrigger}
          disableElevation
          sx={{
            alignSelf: { xs: 'stretch', md: 'flex-end' },
            minHeight: 44,
            // 테마가 contained 버튼에 파란 그라데이션(background)을 깔아 bgcolor 만으로는 안 바뀐다.
            background: targetColor,
            color: target === 'remote' ? '#1a1200' : '#fff',
            fontWeight: 700,
            textTransform: 'none',
            px: 3,
            borderRadius: 1.5,
            transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 150ms',
            '&:hover': {
              background: targetColor,
              transform: 'translateY(-1px)',
              boxShadow: `0 8px 20px ${alpha(targetColor, 0.28)}`,
            },
            '&:active': { transform: 'translateY(0)' },
            '&:focus-visible': { outline: `2px solid ${targetColor}`, outlineOffset: 2 },
          }}
          data-testid="deploy-trigger-btn"
        >
          {TARGET_LABEL[target]}
        </Button>
      </Stack>

      {/* 어디로 배포하는지 — 고른 카드에 실제 대상 서버와 그곳의 마지막 배포를 적는다. */}
      <Box
        role="radiogroup"
        aria-label="배포 대상"
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}
      >
        <TargetCard
          Icon={LanIcon}
          label="사내 테스트 서버"
          sub="빌드해서 사내 서버에 바로 반영"
          where={companyWhere}
          last={latestDeploy(all, 'company')}
          color={colors.accent}
          active={target === 'company'}
          onSelect={() => setTarget('company')}
          testId="deploy-target-company"
        />
        <TargetCard
          Icon={FieldIcon}
          label="현장 통합서버"
          sub="사내에서 확인한 이미지를 현장으로"
          where={remoteWhere}
          last={latestDeploy(all, 'remote')}
          color={colors.warn}
          active={target === 'remote'}
          onSelect={() => setTarget('remote')}
          testId="deploy-target-remote"
        />
      </Box>

      {/* 지금 어느 쪽을 보고 있는지 — 아래 버전·설정·히스토리가 이 대상 기준이다. 대상 색 띠로
          사내/현장을 페이지에서 바로 구분한다. */}
      <Box
        data-testid="deploy-mode-bar"
        data-target={target}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          borderRadius: 1.5,
          bgcolor: alpha(targetColor, 0.14),
          borderLeft: `6px solid ${targetColor}`,
          color: colors.text,
        }}
      >
        {target === 'remote' ? <FieldIcon sx={{ color: targetColor }} /> : <LanIcon sx={{ color: targetColor }} />}
        <Typography sx={{ fontWeight: 800, color: targetColor }}>
          {target === 'remote' ? '현장 통합서버 (원격)' : '사내 테스트 서버'}
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: colors.muted }}>
          {target === 'remote' ? '— 실제 현장에 반영됩니다' : '— 빌드해서 사내 서버(102)에만 반영'}
        </Typography>
      </Box>

      <Box sx={{ pl: { md: 1.5 }, borderLeft: { md: `2px solid ${alpha(targetColor, 0.4)}` } }}>
        <RunningVersionsPanel projectId={projectId} projectName={projectName} target={target} />
      </Box>

      {/* 자정 자동 배포 — 현장 통합서버를 고른 버전으로 맞춰 두는 예약. 결과 배치는 같은 진행 모달로 연다. */}
      <AutoDeployCard projectId={projectId} onOpenBatch={(batchId) => actions.openBatch(site, batchId)} />

      {/* 하단: 배포 히스토리 — 사내/현장을 탭으로 나누고 서비스별로 거른다. */}
      <Paper
        variant="outlined"
        sx={{ p: `${space.md}px`, borderRadius: 2, bgcolor: colors.surface, borderColor: colors.border }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              {SCOPE_LABEL[historyScope]} 배포 히스토리
            </Typography>
            <Typography variant="caption" color="text.secondary" data-testid="deploy-history-count">
              {filtered.length > HISTORY_ROWS
                ? `${filtered.length}건 중 최근 ${HISTORY_ROWS}건`
                : `${filtered.length}건`}
            </Typography>
          </Box>
          {!isLoading && (
            <Button
              size="small"
              onClick={() => refetch()}
              sx={{ textTransform: 'none', minHeight: 44 }}
              data-testid="deploy-history-refresh-btn"
            >
              새로고침
            </Button>
          )}
        </Stack>

        <Tabs
          value={historyScope}
          onChange={(_, v: DeployTabType) => setHistoryScope(v)}
          aria-label="배포 히스토리 대상"
          sx={{ minHeight: 44, borderBottom: `1px solid ${colors.border}`, mb: 1.25 }}
        >
          {(['company', 'remote'] as const).map((s) => (
            <Tab
              key={s}
              value={s}
              label={`${SCOPE_LABEL[s]} 서버${s === 'company' ? '' : '(통합서버)'}`}
              sx={{ minHeight: 44, textTransform: 'none', fontWeight: 700 }}
              data-testid={`deploy-history-tab-${s}`}
            />
          ))}
        </Tabs>

        {apps.length > 0 && (
          // 서비스별 히스토리 — 칩 하나만 고른다. 폰 폭에선 칩 줄만 옆으로 민다.
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ mb: 1.25, overflowX: 'auto', pb: 0.5 }}
            role="group"
            aria-label="서비스별 히스토리"
          >
            <Chip
              label="전체"
              onClick={() => setHistoryApp(null)}
              color={historyApp === null ? 'primary' : 'default'}
              variant={historyApp === null ? 'filled' : 'outlined'}
              sx={{ minHeight: 36, flexShrink: 0, fontWeight: 600 }}
              data-testid="deploy-history-app-all"
            />
            {apps.map((a) => (
              <Chip
                key={a.appname}
                label={`${a.label} ${a.count}`}
                onClick={() => setHistoryApp(a.appname)}
                color={historyApp === a.appname ? 'primary' : 'default'}
                variant={historyApp === a.appname ? 'filled' : 'outlined'}
                sx={{ minHeight: 36, flexShrink: 0, fontWeight: 600 }}
                data-testid={`deploy-history-app-${a.appname}`}
              />
            ))}
          </Stack>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Alert severity="error" variant="outlined">
            배포 히스토리를 불러오지 못했습니다.
          </Alert>
        ) : filtered.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {SCOPE_LABEL[historyScope]} 서버 배포 이력이 없습니다.
          </Typography>
        ) : (
          // 폰 폭에선 표만 옆으로 밀어 보고 화면 전체가 넓어지지 않게 한다.
          <Box sx={{ overflowX: 'auto' }}>
            <HistoryTable items={filtered.slice(0, HISTORY_ROWS)} />
          </Box>
        )}
      </Paper>

      <DeployConfirmDialog
        open={confirmOpen}
        site={site}
        activeSiteName={null}
        branch={actions.branch}
        target={target}
        onClose={closeConfirm}
        onConfirm={async (s, services, options) => {
          await actions.confirmDeploy(s, services, options);
          setConfirmOpen(false);
        }}
      />

      <SiteBatchProgressModal
        open={actions.batchModal.open}
        projectId={actions.batchModal.projectId}
        batchId={actions.batchModal.batchId}
        initialBatch={actions.batchModal.initial}
        siteName={actions.batchModal.siteName}
        onClose={actions.closeBatchModal}
      />

      <AutoSwapConfirmDialog
        open={actions.swapDialog.open}
        otherSite={actions.swapDialog.otherSite}
        targetSite={actions.swapDialog.targetSite}
        onAccept={actions.acceptSwap}
        onReject={actions.rejectSwap}
      />

      <Snackbar
        open={actions.snackbar.open}
        autoHideDuration={4000}
        onClose={actions.dismissSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={actions.dismissSnackbar}
          severity={actions.snackbar.severity}
          variant="filled"
        >
          {actions.snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

/** 그곳의 마지막 배포 한 줄 — "10/29 14:05 · lights v0.1.12 · 완료" */
function lastDeployLine(last: RecentDeploymentItem | null): string {
  if (!last) return '배포 기록 없음';
  const version = last.version && last.version !== 'site-batch' ? `v${last.version}` : '';
  const what = [last.appname, version].filter(Boolean).join(' ');
  return ['마지막 배포', formatTimestamp(last.deployedAt), what, getStatusMeta(last.status).label]
    .filter(Boolean)
    .join(' · ');
}

/**
 * 배포 대상 한 곳 — 무엇(라벨)·어디로(where: 실제 서버)·마지막 배포를 적는다.
 * 고른 쪽은 두꺼운 강조색 테두리와 "배포 대상" 표시, 아닌 쪽은 점선으로 흐리게.
 */
const TargetCard: React.FC<{
  Icon: typeof LanIcon;
  label: string;
  sub: string;
  where: string;
  last: RecentDeploymentItem | null;
  color: string;
  active: boolean;
  onSelect: () => void;
  testId: string;
}> = ({ Icon, label, sub, where, last, color, active, onSelect, testId }) => (
  <Box
    component="button"
    type="button"
    role="radio"
    aria-checked={active}
    onClick={onSelect}
    data-testid={testId}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 0.5,
      minWidth: 0,
      p: 1.5,
      textAlign: 'left',
      borderRadius: 2,
      border: active ? `2px solid ${color}` : `1px dashed ${alpha(color, 0.45)}`,
      bgcolor: active ? alpha(color, 0.08) : 'transparent',
      color: 'text.primary',
      font: 'inherit',
      cursor: 'pointer',
      opacity: active ? 1 : 0.7,
      transition: 'background-color 150ms, opacity 150ms',
      '&:hover': { opacity: 1, bgcolor: alpha(color, 0.05) },
      '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
      <Icon sx={{ fontSize: '1.1rem', color }} />
      <Typography component="span" sx={{ fontSize: '0.95rem', fontWeight: 700, color }}>
        {label}
      </Typography>
      {active && (
        <Typography
          component="span"
          sx={{ ml: 'auto !important', px: 0.75, borderRadius: 1, bgcolor: color, color: '#fff', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0 }}
        >
          배포 대상
        </Typography>
      )}
    </Stack>
    <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
      {sub}
    </Typography>
    <Typography
      component="span"
      sx={{ fontSize: '0.85rem', fontWeight: 600, overflowWrap: 'anywhere' }}
      data-testid={`${testId}-where`}
    >
      {where}
    </Typography>
    <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', overflowWrap: 'anywhere' }}>
      {lastDeployLine(last)}
    </Typography>
  </Box>
);

interface HistoryTableProps {
  items: RecentDeploymentItem[];
}

const HistoryTable: React.FC<HistoryTableProps> = ({ items }) => {
  return (
    <Table size="small" data-testid="deploy-history-table">
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 100 }}>상태</TableCell>
          <TableCell>실행자</TableCell>
          <TableCell>서비스</TableCell>
          <TableCell>버전 / 브랜치</TableCell>
          <TableCell sx={{ width: 180 }}>시각</TableCell>
          <TableCell sx={{ width: 60 }} align="right">
            {/* 액션 컬럼 — 실패 행에 GitHub Actions 링크 노출. */}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => {
          const isFailed = item.status === 'failed' || item.status === 'timeout';
          const ghUrl = githubRunUrl(item.githubRunId);
          return (
            <TableRow key={item.id} hover data-testid={`deploy-history-row-${item.id}`}>
              <TableCell>
                <StatusChip status={item.status} />
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <PersonIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary', fontSize: '1rem' }}
                  />
                  <Typography variant="body2">
                    {item.deployedBy ? item.deployedBy : <em>—</em>}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                {item.appname ? (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                    <AppsIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: '0.95rem' }} />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.78rem' }}
                    >
                      {item.appname}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {/* site-batch 행의 version 은 'site-batch' placeholder 라 의미가
                      없다 — 서비스(appname) 컬럼이 실제 정보를 담으므로 '—' 표기. */}
                  {!item.version || item.version === 'site-batch'
                    ? '—'
                    : `v${item.version}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.branch}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatTimestamp(item.deployedAt)}</Typography>
              </TableCell>
              <TableCell align="right">
                {/* 실패 행: failure_reason tooltip + GitHub Actions 직접 링크. */}
                {isFailed ? (
                  <Tooltip
                    title={
                      item.failureReason
                        ? `실패 사유: ${item.failureReason}\n클릭하면 GitHub Actions 로그를 새 탭에서 엽니다.`
                        : '클릭하면 GitHub Actions 로그를 새 탭에서 엽니다.'
                    }
                    arrow
                  >
                    <Link
                      href={ghUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        fontSize: '0.78rem',
                        fontWeight: 600,
                      }}
                      data-testid={`deploy-history-gh-link-${item.id}`}
                    >
                      로그
                      <OpenInNewIcon sx={{ fontSize: '0.85rem' }} />
                    </Link>
                  </Tooltip>
                ) : item.githubRunId ? (
                  <Tooltip title="GitHub Actions 에서 보기" arrow>
                    <Link
                      href={ghUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'text.secondary', display: 'inline-flex' }}
                    >
                      <OpenInNewIcon sx={{ fontSize: '0.95rem' }} />
                    </Link>
                  </Tooltip>
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

type DeployStatus = RecentDeploymentItem['status'];

function StatusChip({ status }: { status: DeployStatus }): React.ReactElement {
  const meta = getStatusMeta(status);
  return (
    <Chip
      icon={<meta.Icon fontSize="small" />}
      label={meta.label}
      size="small"
      color={meta.color}
      variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  );
}

function getStatusMeta(status: DeployStatus): {
  label: string;
  color: 'success' | 'error' | 'warning' | 'default';
  Icon: typeof CheckIcon;
} {
  if (status === 'success') return { label: '완료', color: 'success', Icon: CheckIcon };
  if (status === 'failed') return { label: '실패', color: 'error', Icon: FailIcon };
  if (status === 'in_progress')
    return { label: '진행 중', color: 'warning', Icon: InProgressIcon };
  return { label: status, color: 'default', Icon: CancelledIcon };
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '--';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export default DeploySection;
